import type { PagedResult } from '../models/paged-result';
import type { PublicArticle } from '../models/public-article';
import type { PublicArticleListItem } from '../models/public-article-list-item';
import { apiGet } from './api-client';

/**
 * The blog's anonymous reads.
 *
 * **The language is part of the address, not a projection parameter.** `/kk/blog/x` and
 * `/ru/blog/x` are two pages with their own slugs and their own publication state, which is the
 * whole reason an article is a row per language on the server. RAICRM's `?locale=` projects one
 * translation of a single entry, and that is the model that gives both languages the same URL.
 *
 * There is no way here to ask for a draft. The copy source's `GetEntries` is `AllowAnonymous` with
 * `bool publishedOnly = false`, so omitting the parameter hands drafts to anybody; the routes
 * these call cannot express it at all.
 */
export async function getArticles(
    language: string,
    page: number,
    signal?: AbortSignal
): Promise<PagedResult<PublicArticleListItem>> {
    const query = new URLSearchParams({ page: String(page) });

    return apiGet<PagedResult<PublicArticleListItem>>(
        `/public/articles/${language}?${query.toString()}`,
        { signal }
    );
}

/**
 * One article.
 *
 * A retired slug answers **301** and `fetch` follows it transparently, so what comes back is the
 * article at its current address — which is why the caller compares `article.slug` with the slug
 * in its own URL and replaces the address bar when they differ. A withdrawn article answers
 * **410** and an unknown one **404**; both surface as an `ApiError` carrying that status, and the
 * page tells them apart because they mean different things to a reader who followed a link.
 */
export async function getArticle(
    language: string,
    slug: string,
    signal?: AbortSignal
): Promise<PublicArticle> {
    return apiGet<PublicArticle>(`/public/articles/${language}/${encodeURIComponent(slug)}`, { signal });
}
